import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, UserCheck } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { ago } from '../../lib/format.js';
import { Badge, Button, Callout, Dialog, DialogContent, DialogTrigger, Field, Input, Spinner, useToast } from '../../ui/index.js';

interface Member {
  oid: string;
  email: string;
  name: string | null;
  added_at: string;
  added_by_name: string | null;
  added_self: boolean;
}

/**
 * Who can see a restricted queue's tickets (072).
 *
 * Managed by whoever configures queues and by the queue's own members.
 * Managing the list is not reading the tickets: an admin who adds
 * themselves can, but it says so here, next to their name, for the members
 * to see.
 */
export function QueueMembers({ tenant, queue, trigger }: { tenant: string; queue: { key: string; name: string }; trigger: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const key = ['queue-members', tenant, queue.key];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ members: Member[] }>(`/api/v1/tenants/${tenant}/admin/queues/${queue.key}/members`),
    enabled: open,
  });
  const after = () => {
    void qc.invalidateQueries({ queryKey: key });
    // Their view of the queue changes with the list.
    void qc.invalidateQueries({ queryKey: ['tickets'] });
  };
  const add = useMutation({
    mutationFn: () => api.post(`/api/v1/tenants/${tenant}/admin/queues/${queue.key}/members`, { email: email.trim() }),
    onSuccess: () => {
      setEmail('');
      after();
      toast.success(`Added to ${queue.name}`);
    },
    onError: (e: unknown) => toast.error('Not added', e instanceof ApiError ? e.message : undefined),
  });
  const remove = useMutation({
    mutationFn: (oid: string) => api.del(`/api/v1/tenants/${tenant}/admin/queues/${queue.key}/members/${encodeURIComponent(oid)}`),
    onSuccess: () => {
      after();
      toast.success('Removed — they can no longer see these tickets');
    },
    onError: (e: unknown) => toast.error('Not removed', e instanceof ApiError ? e.message : undefined),
  });

  const members = q.data?.members ?? [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        title={
          <span className="inline-flex items-center gap-1.5">
            <Lock aria-hidden className="h-3.5 w-3.5" />
            Who can see {queue.name}
          </span>
        }
        description="Only these people see this queue's tickets — not other agents, and not administrators unless they are listed here. Each ticket's requester always sees their own."
      >
        {q.isLoading && <Spinner />}
        {q.error && (
          <Callout tone="danger">{q.error instanceof ApiError ? q.error.message : 'The members could not be loaded'}</Callout>
        )}
        {q.data && members.length === 0 && (
          <Callout tone="warn" className="mb-3">
            Nobody is a member yet, so nobody at the desk can see these tickets. Add the people who handle them.
          </Callout>
        )}
        {members.length > 0 && (
          <ul className="mb-3 space-y-1.5">
            {members.map((m) => (
              <li key={m.oid} className="flex items-center gap-2 text-xs">
                <UserCheck aria-hidden className="h-3.5 w-3.5 shrink-0 text-brand" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-ink">
                    {m.name ?? m.email}
                    {m.added_self && (
                      <Badge tone="warn" className="ml-1.5">
                        added themselves
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-2xs text-ink-tertiary">
                    {m.email} · added {ago(m.added_at)}
                    {m.added_by_name && !m.added_self ? ` by ${m.added_by_name}` : ''}
                  </p>
                </div>
                <Button variant="ghost" size="sm" disabled={remove.isPending} onClick={() => remove.mutate(m.oid)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-end gap-2">
          <Field label="Add someone" hint="They must already be an agent in this group.">
            <Input
              size="sm"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="someone@esassoc.com"
              aria-label="Member email"
            />
          </Field>
          <Button size="sm" disabled={email.trim() === '' || add.isPending} loading={add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
